/**
 * Features MVI State
 *
 * State interfaces, initial state factory, and selectors.
 */

import type { IFeature } from "@/shared/types";
import type { IApiError } from "@/shared/types";

// ============================================================================
// State Interface
// ============================================================================

export interface IFeaturesState {
  /** app-shell-work-context-R07: block dependent reads until verification. */
  readonly contextPending?: boolean;
  /**
   * Проект, которому принадлежит загруженный перечень.
   *
   * Выбор фичи принадлежит проекту (business-model-features-R10): чтобы
   * отличить перечитывание перечня своего проекта от прихода перечня другого
   * проекта, состояние помнит, чей перечень оно держит. Пустая строка —
   * перечень не загружен.
   */
  readonly projectId: string;
  /** Перечень фич проекта: продукт отдаёт его от новых к старым (R01). */
  readonly features: readonly IFeature[];
  readonly selectedFeatureId: string;
  /**
   * Фича, скрытая архивацией, но оставшаяся выбранной.
   *
   * В перечень она не входит (R01), а в селекторе встречается единственным
   * образом — как выбранная на момент архивации: показывается, пока
   * пользователь не выберет другую, и после смены выбора в список опций уже
   * не возвращается (business-model-features-R08, R11).
   */
  readonly retainedFeature: IFeature | null;
  /**
   * Перечень **вместе с архивными** — его показывает поверхность управления
   * фичами по переключателю «показывать архивные» (feature-lifecycle-R28).
   * Держится отдельно от `features`, потому что селектор фичи и всё
   * остальное архивных видеть не должны (features-R01, R11).
   */
  readonly featuresWithArchived: readonly IFeature[];
  readonly isLoading: boolean;
  readonly isCreating: boolean;
  readonly isSaving: boolean;
  readonly isDeleting: boolean;
  readonly error: IApiError | null;
}

// ============================================================================
// Initial State
// ============================================================================

export function createInitialState(): IFeaturesState {
  return {
    projectId: "",
    features: [],
    selectedFeatureId: "",
    retainedFeature: null,
    featuresWithArchived: [],
    isLoading: false,
    isCreating: false,
    isSaving: false,
    isDeleting: false,
    error: null,
  };
}

// ============================================================================
// Selectors
// ============================================================================

export function selectFeatures(state: IFeaturesState): readonly IFeature[] {
  return state.features;
}

/** Проект загруженного перечня — им проверяется принадлежность выбора (R10). */
export function selectProjectId(state: IFeaturesState): string {
  return state.projectId;
}

/** Перечень с архивными — только для поверхности управления фичами (R28). */
export function selectFeaturesWithArchived(
  state: IFeaturesState,
): readonly IFeature[] {
  return state.featuresWithArchived;
}

/**
 * Опции селектора фичи: перечень плюс архивная выбранная, если она есть
 * (R11). Иных источников у списка опций нет.
 */
export function selectSelectorOptions(
  state: IFeaturesState,
): readonly IFeature[] {
  const retained = state.retainedFeature;
  if (retained === null) return state.features;
  if (state.features.some((feature) => feature.id === retained.id)) {
    return state.features;
  }
  return [retained, ...state.features];
}

export function selectSelectedFeature(state: IFeaturesState): IFeature | null {
  const selected = state.features.find(
    (feature) => feature.id === state.selectedFeatureId,
  );
  if (selected !== undefined) return selected;
  if (state.retainedFeature?.id === state.selectedFeatureId) {
    return state.retainedFeature;
  }
  return null;
}

export function selectSelectedFeatureId(state: IFeaturesState): string {
  return state.selectedFeatureId;
}

export function selectIsLoading(state: IFeaturesState): boolean {
  return state.isLoading;
}

export function selectIsCreating(state: IFeaturesState): boolean {
  return state.isCreating;
}

export function selectIsSaving(state: IFeaturesState): boolean {
  return state.isSaving;
}

export function selectIsDeleting(state: IFeaturesState): boolean {
  return state.isDeleting;
}

export function selectError(state: IFeaturesState): IApiError | null {
  return state.error;
}
